import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Alert,
  Divider,
  Button,
  Paper,
  CircularProgress
} from '@mui/material';
import api from '../api/axios';
import { outcomeModuleDisplayName } from '../utils/outcomeModuleName';
import {
  getExpectedIacsForModule,
  collectAddressedIacCodesFromAssessments,
  filterTaskScopedAssessments,
  countIacOccurrences,
  findOverAssessedIacs,
  findRepeatedIacTriples,
  groupExpectedIacsByTask
} from '../utils/taskAssessmentIacAudit';
import {
  downloadModuleAuditCsv,
  downloadModuleAuditXlsx,
  downloadModuleAuditPdf
} from '../utils/moduleAssessmentAuditExport';

function kindLabel(kind) {
  const map = {
    mcq: 'MCQ',
    free_text: 'Free text',
    true_false: 'True/False',
    case_study: 'Case study'
  };
  return map[kind] || kind;
}

export default function ModuleAssessmentAuditPanel({
  qualificationId,
  module,
  assignedItems,
  parentAssessmentListVersion
}) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coverageReport, setCoverageReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const loadAssessments = useCallback(async () => {
    if (!qualificationId || !module?.id) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/qualifications/${qualificationId}/learning-content/task-assessments`,
        { params: { module_id: module.id } }
      );
      setAssessments(res.data?.assessments || []);
    } catch (_) {
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  }, [qualificationId, module?.id]);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments, parentAssessmentListVersion]);

  const expectedIacs = useMemo(
    () => getExpectedIacsForModule(module?.id, assignedItems || {}),
    [module?.id, assignedItems]
  );

  const expectedCodes = useMemo(() => expectedIacs.map((i) => i.code), [expectedIacs]);

  const taskAssessments = useMemo(
    () => filterTaskScopedAssessments(assessments),
    [assessments]
  );

  const taskAddressedUnion = useMemo(
    () => collectAddressedIacCodesFromAssessments(taskAssessments),
    [taskAssessments]
  );

  const missingTaskIacs = useMemo(() => {
    const addr = new Set(taskAddressedUnion);
    return expectedCodes.filter((c) => !addr.has(c));
  }, [expectedCodes, taskAddressedUnion]);

  const iacOccurrences = useMemo(() => countIacOccurrences(taskAssessments), [taskAssessments]);
  const overAssessed = useMemo(() => findOverAssessedIacs(iacOccurrences, 2), [iacOccurrences]);
  const repeatedTriples = useMemo(() => findRepeatedIacTriples(iacOccurrences, 3), [iacOccurrences]);

  const taskGroups = useMemo(() => {
    const groups = groupExpectedIacsByTask(expectedIacs);
    const addr = new Set(taskAddressedUnion);
    return groups.map((g) => ({
      ...g,
      addressedCount: g.iacs.filter((i) => addr.has(i.code)).length,
      totalCount: g.iacs.length
    }));
  }, [expectedIacs, taskAddressedUnion]);

  const coveragePercent = expectedCodes.length
    ? Math.round((taskAddressedUnion.length / expectedCodes.length) * 100)
    : 100;

  const moduleTitle = outcomeModuleDisplayName(module);

  const exportData = useMemo(() => {
    const safeName = String(moduleTitle).replace(/[^a-z0-9-_]+/gi, '_').slice(0, 80);
    const filenameBase = `assessment-audit-${module?.id ?? 'module'}-${qualificationId}-${safeName}`;
    const summaryTaskRows = [
      ['Metric', 'Value'],
      ['IACs in curriculum (this module)', String(expectedCodes.length)],
      ['IACs addressed in task assessments (union)', String(taskAddressedUnion.length)],
      ['IACs missing from task assessments', String(missingTaskIacs.length)],
      ['Coverage %', String(coveragePercent)],
      ['IACs assessed 2+ times', String(overAssessed.length)],
      ['IACs assessed 3+ times', String(repeatedTriples.length)]
    ];
    const expectedIacTaskRows = [
      ['IAC code', 'Parent task code', 'Parent task title', 'Addressed in a task-scoped assessment?'],
      ...expectedIacs.map((i) => [
        i.code,
        i.task_code,
        i.task_title,
        taskAddressedUnion.includes(i.code) ? 'Yes' : 'No'
      ])
    ];
    return {
      moduleTitle,
      moduleId: module?.id,
      filenameBase,
      summaryTaskRows,
      expectedIacTaskRows
    };
  }, [
    moduleTitle,
    module?.id,
    qualificationId,
    expectedCodes.length,
    expectedIacs,
    taskAddressedUnion,
    missingTaskIacs.length,
    coveragePercent,
    overAssessed.length,
    repeatedTriples.length
  ]);

  const generateCoverageReport = async () => {
    if (!qualificationId || !module?.id) return;
    setReportLoading(true);
    setCoverageReport(null);
    try {
      const res = await api.post(
        `/qualifications/${qualificationId}/learning-content/task-assessments/coverage-report`,
        {
          module_id: module.id,
          expected_iac_codes: expectedCodes,
          expected_iacs: expectedIacs
        }
      );
      setCoverageReport(res.data?.report || null);
    } catch (e) {
      alert(e.response?.data?.message || e.message || 'Coverage report failed');
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
        Assessment audit
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Module: {moduleTitle} (id {module?.id}). Coverage below is for task-scoped assessments only.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" onClick={() => downloadModuleAuditCsv(exportData)}>
          Export CSV
        </Button>
        <Button size="small" variant="outlined" onClick={() => downloadModuleAuditXlsx(exportData)}>
          Export Excel
        </Button>
        <Button size="small" variant="outlined" onClick={() => downloadModuleAuditPdf(exportData)}>
          Export PDF
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={reportLoading || loading}
          onClick={generateCoverageReport}
        >
          {reportLoading ? 'Generating…' : 'Generate coverage report'}
        </Button>
      </Stack>

      {loading ? (
        <Typography color="text.secondary">Loading assessments…</Typography>
      ) : (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Coverage summary
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Chip label={`${coveragePercent}% IAC coverage`} color={coveragePercent >= 100 ? 'success' : 'warning'} size="small" />
            <Chip label={`${expectedCodes.length} IACs in curriculum`} size="small" />
            <Chip label={`${taskAddressedUnion.length} addressed`} color="primary" size="small" variant="outlined" />
            <Chip
              label={`${missingTaskIacs.length} missing`}
              color={missingTaskIacs.length ? 'warning' : 'success'}
              size="small"
            />
            <Chip
              label={`${overAssessed.length} over-assessed (2+)`}
              color={overAssessed.length ? 'warning' : 'default'}
              size="small"
            />
            <Chip
              label={`${repeatedTriples.length} assessed 3+ times`}
              color={repeatedTriples.length ? 'error' : 'default'}
              size="small"
            />
          </Stack>

          {coverageReport && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                AI coverage report
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {coverageReport.summary}
              </Typography>
              {coverageReport.recommendations?.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Recommendations
                  </Typography>
                  <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                    {coverageReport.recommendations.map((r, i) => (
                      <li key={i}>
                        <Typography variant="body2">{r}</Typography>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Paper>
          )}

          {reportLoading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Analyzing coverage…
              </Typography>
            </Stack>
          )}

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Per-task coverage
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>IACs addressed</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {taskGroups.map((g) => (
                <TableRow key={g.task_code}>
                  <TableCell>
                    {g.task_code} — {g.task_title}
                  </TableCell>
                  <TableCell>
                    {g.addressedCount} / {g.totalCount}
                  </TableCell>
                  <TableCell>
                    {g.addressedCount === g.totalCount ? (
                      <Chip size="small" label="Complete" color="success" />
                    ) : g.addressedCount === 0 ? (
                      <Chip size="small" label="Not assessed" color="error" />
                    ) : (
                      <Chip size="small" label="Partial" color="warning" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {missingTaskIacs.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                IACs not addressed in any task-scoped assessment
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {missingTaskIacs.map((c) => {
                  const meta = expectedIacs.find((i) => i.code === c);
                  return (
                    <Chip
                      key={c}
                      size="small"
                      label={`${c}${meta?.task_code ? ` · task ${meta.task_code}` : ''}`}
                    />
                  );
                })}
              </Stack>
            </Alert>
          )}

          {overAssessed.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                IAC duplication
              </Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>IAC code</TableCell>
                    <TableCell>Occurrences</TableCell>
                    <TableCell>Question IDs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overAssessed.map((o) => (
                    <TableRow key={o.code}>
                      <TableCell>{o.code}</TableCell>
                      <TableCell>{o.count}</TableCell>
                      <TableCell>{(o.questionIds || []).join(', ')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Task assessments
          </Typography>
          {taskAssessments.length === 0 ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No task-scoped assessments for this module. Use the Assessments tab to create them.
            </Typography>
          ) : (
            <Table size="small" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Questions</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taskAssessments.map((r) => {
                  const qs = r.payload?.questions || [];
                  const n = qs.length;
                  const marks = qs.reduce((s, q) => s + (Number(q.marks) || 0), 0);
                  return (
                    <TableRow key={r.id}>
                      <TableCell>{r.task_code}</TableCell>
                      <TableCell>{kindLabel(r.kind)}</TableCell>
                      <TableCell>{n}</TableCell>
                      <TableCell>{marks}</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.status} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Expected IACs — task assessments
          </Typography>
          <Table size="small" sx={{ mb: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>In task assessment?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expectedIacs.map((i) => (
                <TableRow key={i.code}>
                  <TableCell>{i.code}</TableCell>
                  <TableCell>
                    {i.task_code} — {i.task_title}
                  </TableCell>
                  <TableCell>
                    {taskAddressedUnion.includes(i.code) ? (
                      <Chip size="small" label="Yes" color="success" />
                    ) : (
                      <Chip size="small" label="No" color="warning" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}
